import { createContext, useContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';
import { getEnabledLanguages, getDefaultLanguage } from '@/shared/i18n';
import type { SupportedLanguage } from '@/shared/lib/language';

/**
 * I18n Provider — real implementation backed by react-i18next.
 *
 * Contract (unchanged from the foundation stub):
 *   - `SupportedLanguage`: 'en' | 'de'
 *   - `I18nProvider({ children, defaultLanguage? })`: wraps the tree
 *   - `useI18n()`: returns `{ language, setLanguage, t }`
 *
 * Implementation details:
 *   - Persists selected language to localStorage ('saas:lang').
 *   - Detects initial language from: localStorage → navigator.language → env default.
 *   - `setLanguage` switches the i18next instance language and persists the choice.
 *   - `t` delegates to react-i18next's `useTranslation` (common namespace).
 *   - `I18nextProvider` supplies the shared i18n instance to all child hooks.
 */

export type { SupportedLanguage };

const STORAGE_KEY = 'saas:lang';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface I18nContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

// ---------------------------------------------------------------------------
// Internal hook — bridges react-i18next and the context value
// ---------------------------------------------------------------------------

function useI18nBridge(initialLanguage: SupportedLanguage): I18nContextValue {
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const { t: tFn } = useTranslation('common');

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    const enabledCodes = getEnabledLanguages().map((l) => l.code);
    if (!enabledCodes.includes(lang)) return; // ignore disabled languages
    void i18n.changeLanguage(lang);
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage may be unavailable in some environments
    }
  }, []);

  // Keep local state in sync if i18next language changes externally
  useEffect(() => {
    const handler = (lang: string) => {
      if (lang === 'en' || lang === 'de') {
        setLanguageState(lang as SupportedLanguage);
      }
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  const t = useCallback(
    (key: string, options?: Record<string, unknown>) => tFn(key, options) as string,
    [tFn],
  );

  return { language, setLanguage, t };
}

// ---------------------------------------------------------------------------
// Provider implementation
// ---------------------------------------------------------------------------

interface I18nProviderProps {
  children: ReactNode;
  /** Initial language. Defaults to detected or first-enabled language. */
  defaultLanguage?: SupportedLanguage;
}

function I18nInner({ children, defaultLanguage }: I18nProviderProps) {
  // Resolve initial language: prop → localStorage → browser hint → env default
  const resolvedInitial = ((): SupportedLanguage => {
    if (defaultLanguage) return defaultLanguage;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'de') return stored;
    } catch {
      // ignore
    }
    const browserLang = navigator.language.slice(0, 2).toLowerCase();
    if (browserLang === 'de') return 'de';
    return getDefaultLanguage() as SupportedLanguage;
  })();

  // Set i18next language synchronously before first render
  if (i18n.language !== resolvedInitial) {
    i18n.changeLanguage(resolvedInitial);
  }

  const value = useI18nBridge(resolvedInitial);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function I18nProvider({ children, defaultLanguage }: I18nProviderProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <I18nInner defaultLanguage={defaultLanguage}>{children}</I18nInner>
    </I18nextProvider>
  );
}

/** Consume the i18n context. Throws if used outside I18nProvider. */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}
